// Корень App (SPEC §4.0). Собирает провайдеры в порядке зависимости и поднимает фронт-стек один раз
// на сессию:
//   AppThemeProvider (тема, мгновенно, без сети)
//     → TransportProvider (Transport создан, start())
//       → StoreProvider (store.attach(transport); hydrate() ДО первой отрисовки — офлайн-первый рендер)
//         → Router → PwaProviderShell (SW, deepLink→navigate, badge, install)
//           → Notifications/UserCache
//             → SessionProvider (ЕДИНЫЙ источник self: me()/login/logout)
//               → NotifySettings/ActiveChat → AuthGate → AppShell
//
// Транспорт/стор инъецируемы (тесты подставляют фейки). Жизненный цикл: detach/stop на размонтировании
// БЕЗ чистки кеша (офлайн-чтение переживает перезагрузку, §4.0 п.6).

import { useEffect, useRef, useState } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { createStore, type ClientStore } from '@tele-chat/client-store'
import { createConnectionDiag, createTransport, type Transport } from '@tele-chat/client-transport'
import type { SelfUser } from '@tele-chat/shared-contracts'
import type { ThemePreference } from '@tele-chat/design-system'
import { readAppConfig } from '../config.js'
import { AppThemeProvider } from './ThemePreferenceContext.js'
import { DiagProvider, type DiagApi } from './DiagContext.js'
import { TransportProvider } from '../transport/TransportContext.js'
import { StoreProvider, useStore } from '../store/StoreContext.js'
import { PwaProviderShell } from '../pwa/PwaContext.js'
import { SessionProvider, useSession } from '../auth/SessionContext.js'
import { UserCacheProvider } from '../users/UserCacheContext.js'
import { NotifySettingsProvider } from '../settings/NotifySettingsContext.js'
import { NotificationsProvider } from '../notifications/NotificationsContext.js'
import { ActiveChatProvider } from './ActiveChatContext.js'
import { MediaTransfersProvider } from '../media/MediaTransfersContext.js'
import { AppShell } from './AppShell.js'
import { AuthScreen } from '../screens/AuthScreen.js'
import { ForcePasswordChangeScreen } from '../screens/ForcePasswordChangeScreen.js'
import { EmptyState } from '../components/EmptyState.js'
import { strings } from '../i18n/strings.js'

export interface AppProps {
  /** Инъекция транспорта (тесты/SSR). По умолчанию — createTransport({baseUrl}). */
  transport?: Transport
  /** Инъекция стора (тесты). По умолчанию — createStore(). */
  store?: ClientStore
  /** Отключить реальную PWA-регистрацию (тесты). */
  pwaEnabled?: boolean
  /** Принудительная тема (тесты). */
  theme?: ThemePreference
  /** Пропустить стартовый me() и считать пользователя этим (тесты): null → сразу экран входа. */
  bootstrapSelf?: SelfUser | null
}

/** Единственный экземпляр транспорта/стора на жизнь приложения (ленивая инициализация в ref). */
function useFrontStack(props: AppProps): { transport: Transport; store: ClientStore; diag: DiagApi } {
  const ref = useRef<{ transport: Transport; store: ClientStore; diag: DiagApi } | null>(null)
  if (ref.current === null) {
    let transport = props.transport
    // Вне реальной композиции (тесты подставляют транспорт) диагностики нет — no-op.
    let diag: DiagApi = { log: () => {}, flush: () => {} }
    if (transport === undefined) {
      // Реальная композиция: сетевые порты транспорта оборачиваются «чёрным ящиком» диагностики
      // (client-diag): WS-жизненный цикл с кодами закрытия, сбои REST, пробы /healthz при разрыве —
      // в localStorage-буфер, при появлении связи досылается на POST /diag/client-log.
      const { baseUrl, wsFallbackUrls } = readAppConfig()
      const connDiag = createConnectionDiag({ baseUrl })
      transport = createTransport({
        baseUrl,
        wsFallbackUrls, // мульти-edge: авто-переключение WS между входами (см. config.ts)
        fetchImpl: connDiag.wrapFetch(),
        wsFactory: connDiag.wrapWs(),
      })
      connDiag.start()
      // Экраны могут дописывать доменные маркеры (напр. содержимое буфера при вставке) — через контекст.
      diag = { log: (ev, d) => connDiag.log(ev, d), flush: () => void connDiag.flushNow() }
    }
    const store = props.store ?? createStore()
    ref.current = { transport, store, diag }
  }
  return ref.current
}

export function App(props: AppProps = {}): JSX.Element {
  const { transport, store, diag } = useFrontStack(props)
  const [booted, setBooted] = useState(false)
  const [initialSelf, setInitialSelf] = useState<SelfUser | null>(props.bootstrapSelf ?? null)
  const [checking, setChecking] = useState(props.bootstrapSelf === undefined)

  // Жизненный цикл старта (порядок §4.0): hydrate → attach → start → me().
  useEffect(() => {
    let cancelled = false
    async function boot(): Promise<void> {
      try {
        await store.hydrate() // 1) офлайн-кеш ДО отрисовки данных
      } catch {
        /* нет кеша — рисуем пусто */
      }
      store.attach(transport) // 2) запуск цикла transport.events()
      try {
        await transport.start() // 3) живой канал
      } catch {
        /* офлайн-старт — стор работает из кеша */
      }
      if (cancelled) return
      setBooted(true)

      if (props.bootstrapSelf === undefined) {
        try {
          const me = await transport.rest.me() // 4) проверка сессии
          if (!cancelled) setInitialSelf(me)
        } catch {
          if (!cancelled) setInitialSelf(null)
        } finally {
          if (!cancelled) setChecking(false)
        }
      }
    }
    void boot()
    return () => {
      cancelled = true
      store.detach() // detach/stop БЕЗ чистки кеша (§4.0 п.6)
      void transport.stop()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Пока идёт boot/me — заглушка (офлайн-первый рендер уже произошёл из кеша на следующем шаге).
  return (
    <AppThemeProvider initial={props.theme}>
      <TransportProvider transport={transport}>
        <StoreProvider store={store}>
          <DiagProvider api={diag}>
          <BrowserRouter>
            <PwaProviderShell enabled={props.pwaEnabled ?? true}>
              <NotificationsProvider>
                <MediaTransfersProvider>
                <UserCacheProvider>
                  <SessionProvider
                    initialSelf={initialSelf}
                    initialChecking={checking}
                    onAuthenticated={async () => {
                      try {
                        await transport.start()
                      } catch {
                        /* канал поднимется при сети */
                      }
                    }}
                    onPasswordChanged={async () => {
                      // PWD-4: пока пароль не был сменён, стартовая синхронизация стора (GET /chats) получила
                      // 403 и пропущена — перечитываем список чатов уже с доступом. Канал НЕ перезапускаем:
                      // transport.stop() закрывает поток событий, на котором живёт стор.
                      try {
                        for (const chat of await transport.rest.listChats()) store.actions.ingestChat(chat)
                      } catch {
                        /* список подтянется при следующем переподключении */
                      }
                    }}
                  >
                    <SessionScopedProviders>
                      <AuthGate booted={booted} checking={checking} />
                    </SessionScopedProviders>
                  </SessionProvider>
                </UserCacheProvider>
                </MediaTransfersProvider>
              </NotificationsProvider>
            </PwaProviderShell>
          </BrowserRouter>
          </DiagProvider>
        </StoreProvider>
      </TransportProvider>
    </AppThemeProvider>
  )
}

/** Провайдеры, зависящие от сессии: настройки уведомлений (durable при self≠null) + активный чат. */
function SessionScopedProviders({ children }: { children: JSX.Element }): JSX.Element {
  const { self } = useSession()
  const store = useStore()
  // Держим selfId стора в синхроне с сессией: стор создан ДО логина, иначе его selfId навсегда
  // undefined (ломает оптимистичные реакции/«mine»/подавление своего typing).
  useEffect(() => {
    store.setSelf(self)
  }, [self, store])
  return (
    // PWD-4: пока пароль не сменён, API закрыт (PWD-3) — настройки уведомлений не грузим.
    <NotifySettingsProvider enabled={self !== null && self.mustChangePassword !== true}>
      <ActiveChatProvider>{children}</ActiveChatProvider>
    </NotifySettingsProvider>
  )
}

/** AuthGate (§4.6): по checking/self выбирает экран входа vs приложение. self — из SessionContext. */
function AuthGate({ booted, checking }: { booted: boolean; checking: boolean }): JSX.Element {
  const { self, checking: sessionChecking } = useSession()
  if (!booted || checking || sessionChecking) {
    return (
      <div style={{ height: '100%' }}>
        <EmptyState title={strings.app.loading} />
      </div>
    )
  }
  if (self === null) {
    return <AuthScreen />
  }
  // PWD-4: пароль задал администратор — сначала свой пароль, потом приложение.
  if (self.mustChangePassword === true) {
    return <ForcePasswordChangeScreen />
  }
  return <AppShell />
}
